import {
  Box,
  BoxProps,
  CollapsibleIconButton,
  FormControl,
  IconButton,
  InputLabel,
  Stack,
  Tooltip,
  Typography,
} from '@chronosphereio/chrono-ui';
import { produce } from 'immer';
import {
  ContentCopy as CopyIcon,
  DeleteOutline as DeleteIcon,
  EyeOutline as VisibilityIcon,
  EyeOffOutline as VisibilityOffIcon,
} from 'mdi-material-ui';
import React, { useMemo } from 'react';
import {
  ChronosphereDataQueryDefinition,
  indexToAlpha,
  QueryDefinition,
  SupportedDataType,
  TimeSeriesQueryDefinition,
} from '@/core';
import { OptionsEditorOnChangeContext, VariableOpportunity } from '@/plugin-system/model';
import {
  doesPluginSupportDataType,
  getVariableOpportunitiesFunction,
  initializeDataQueryPlugin,
  useListPluginMetadata,
  usePlugin,
  useVariableOpportunityContext,
} from '@/plugin-system/runtime';
import { PluginKindSelect } from '../../PluginKindSelect';
import { PluginEditor, PluginEditorProps, usePluginEditor } from '../PluginEditor';
import { AliasInput } from './AliasInput';

const EMPTY_OPPORTUNITIES: VariableOpportunity[] = [];

export interface DataQueryInputProps {
  query: QueryDefinition;
  index: number;
  onChange: (index: number, query: QueryDefinition, ctx?: OptionsEditorOnChangeContext) => void;
  onCollapseExpand: (index: number) => void;
  isCollapsed?: boolean;
  onDelete?: (index: number) => void;
  onDuplicate: (index: number) => void;
  onToggleDisabled: (index: number) => void;
  dataType?: SupportedDataType;
  renderAdditionalControls?: (
    definition: ChronosphereDataQueryDefinition,
    args: {
      /**
       * The index of the query in the list of queries
       */
      idx: number;
    }
  ) => React.ReactNode;
  renderPluginKindSelector?: (kind: string) => React.ReactNode;
  renderAdditionalActions?: (
    query: QueryDefinition,
    args: {
      /**
       * The index of the query in the list of queries
       */
      idx: number;
    }
  ) => React.ReactNode;
}

export const DataQueryInput = ({
  index,
  query,
  isCollapsed,
  onDelete,
  onDuplicate,
  onChange,
  onCollapseExpand,
  onToggleDisabled,
  dataType,
  renderAdditionalControls,
  renderPluginKindSelector,
  renderAdditionalActions,
}: DataQueryInputProps) => {
  const queryName = indexToAlpha(index);

  const { onKindChange, pendingKind, isLoading, error } = usePluginEditor({
    pluginType: 'DataQuery',
    value: query,
    onChange: (next, ctx) => {
      onChange(
        index,
        produce(query, (draft) => {
          draft.spec.plugin = next as ChronosphereDataQueryDefinition;
        }),
        ctx
      );
    },
  });

  const handleAliasChange = (alias: string) => {
    onChange(
      index,
      produce(query, (draft) => {
        draft.spec.alias = alias.trim();
      }),
      {
        shouldRunQuery: false,
      }
    );
  };

  const currentDataQueryPluginKind = pendingKind !== '' ? pendingKind : query?.spec?.plugin?.kind;
  const opportunityCtx = useVariableOpportunityContext();
  const { data: pluginImplementation } = usePlugin('DataQuery', query.spec.plugin.kind, {
    enabled: opportunityCtx !== undefined,
  });

  // Disable query input if query is not compatible with data type
  const { data: listPluginMetadata } = useListPluginMetadata('DataQuery');
  const pluginMetadata = listPluginMetadata?.find((plugin) => plugin.kind === currentDataQueryPluginKind);
  const isPluginCompatibleWithDataType = doesPluginSupportDataType({ validDataType: dataType, plugin: pluginMetadata });

  const pluginInstance = useMemo(() => {
    if (!opportunityCtx || !pluginImplementation) return undefined;
    return initializeDataQueryPlugin(pluginImplementation);
  }, [opportunityCtx, pluginImplementation]);

  const variableOpportunities = useMemo<VariableOpportunity[]>(() => {
    if (!opportunityCtx || !pluginInstance) return EMPTY_OPPORTUNITIES;

    const getVariableOpportunities = getVariableOpportunitiesFunction(pluginInstance);
    if (!getVariableOpportunities) return EMPTY_OPPORTUNITIES;

    return getVariableOpportunities.call(pluginInstance, query.spec.plugin.spec, opportunityCtx);
  }, [opportunityCtx, pluginInstance, query.spec.plugin.spec]);

  const isDisabled = query.spec.disabled === true;

  const leftMarginPx = 28;
  const queryRefBoxSize = 32; // Same size as the input

  const opacity = isDisabled || !isPluginCompatibleWithDataType ? 0.4 : 1;
  return (
    <div>
      <Box display="flex" alignItems="center">
        <Box width={leftMarginPx}>
          <CollapsibleIconButton
            collapsed={isCollapsed}
            // We want the collapsible icon and query name to be clickable. We don't want
            // the rest of the query header to be clickable, because (a) currently,
            // we trigger running queries by clicking outside of the query input
            // and (b) there is a bug where collapse/expand is causing the loss
            // of query input edits.
            onClick={() => onCollapseExpand(index)}
          />
        </Box>
        <Stack direction="row" flex={1}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ opacity: opacity }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius={1}
              width={queryRefBoxSize}
              height={queryRefBoxSize}
              sx={{
                backgroundColor: (t) => t.palette.status.default.lowContrast,
              }}
            >
              <Typography variant="body1" fontWeight={'bold'}>
                {queryName}
              </Typography>
            </Box>
            {renderPluginKindSelector ? (
              renderPluginKindSelector(query.spec.plugin.kind)
            ) : (
              <FormControl margin="dense" fullWidth={false} disabled={isLoading} error={error !== null} sx={{ mb: 1 }}>
                <InputLabel id="plugin-kind-label">Datasource</InputLabel>
                <PluginKindSelect
                  disabled={!isPluginCompatibleWithDataType}
                  labelId="plugin-kind-label"
                  label={'Datasource'}
                  pluginType={'DataQuery'}
                  value={currentDataQueryPluginKind}
                  onChange={onKindChange}
                  validDataType={dataType}
                />
              </FormControl>
            )}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1} marginLeft="auto">
            {renderAdditionalActions?.(query, { idx: index })}
            <AliasInput alias={query.spec.alias ?? ''} onChange={handleAliasChange} />
            <Tooltip Content={`Duplicate query ${queryName}`}>
              <IconButton size="small" aria-label={`Duplicate query ${queryName}`} onClick={() => onDuplicate(index)}>
                <CopyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip Content={isDisabled ? `Show query ${queryName}` : `Hide query ${queryName}`}>
              <IconButton
                size="small"
                aria-label={isDisabled ? `Show query ${queryName}` : `Hide query ${queryName}`}
                onClick={() => onToggleDisabled(index)}
              >
                {isDisabled ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </Tooltip>
            {onDelete && (
              <Tooltip Content={`Delete query ${queryName}`}>
                <IconButton
                  size="small"
                  aria-label={`Delete query ${queryName}`}
                  onClick={() => onDelete && onDelete(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Box>
      {!isCollapsed && (
        <Box sx={{ marginLeft: leftMarginPx + 'px', my: 1, opacity: opacity }}>
          <QueryEditor
            value={query}
            onChange={(nextQuery, ctx) => onChange(index, nextQuery as QueryDefinition, ctx)}
            dataType={dataType}
            variableOpportunities={variableOpportunities}
          />
          {renderAdditionalControls?.(query.spec.plugin, { idx: index })}
        </Box>
      )}
    </div>
  );
};

// Props on MUI Box that we don't want people to pass because we're either redefining them or providing them in
// this component
type OmittedMuiProps = 'children' | 'value' | 'onChange';

interface QueryEditorProps extends Omit<BoxProps, OmittedMuiProps | 'ref'> {
  value: TimeSeriesQueryDefinition;
  onChange: PluginEditorProps['onChange'];
  dataType?: SupportedDataType;
  variableOpportunities?: PluginEditorProps['variableOpportunities'];
}

/**
 * Displays an editor for TimeSeriesQueryDefinition objects.
 */
function QueryEditor(props: QueryEditorProps) {
  const { value, onChange, dataType, variableOpportunities, ...others } = props;
  const {
    spec: { plugin },
  } = value;

  const handlePluginChange: PluginEditorProps['onChange'] = (next, ctx) => {
    onChange(
      produce(value, (draft) => {
        // This needs to be casted because the PluginEditor operates on generic plugin definitions.
        draft.spec.plugin = next as ChronosphereDataQueryDefinition;
      }),
      ctx
    );
  };

  return (
    <Box {...others}>
      {/* If TimeSeriesQuery plugins ever have common props on the definition, the inputs could go here */}
      <PluginEditor
        hidePluginKindSelector
        pluginType="DataQuery"
        pluginKindLabel="Query type"
        value={plugin}
        onChange={handlePluginChange}
        dataType={dataType}
        variableOpportunities={variableOpportunities}
      />
    </Box>
  );
}
